import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ButtonPreview from "./ButtonPreview";
import DetailsViewPreview from "./DetailsViewPreview";
import DropdownPreview from "./DropdownPreview";
import FieldPreview from "./FieldPreview";
import { ImagePreview } from "./ImagePreview";
import LabelPreview from "./LabelPreview";
import RecordsViewPreview from "./RecordsViewPreview";
import SeparatorPreview from "./SeparatorPreview";

import { useAppBlocksContext } from "../../context/AppBlocksContext";
import MaterialIcon from "../BuilderComponents/MaterialIcon";
import ActivityCardPreview from "./ActivityCardPreview";
import ButtonGroupPreview from "./ButtonGroupPreview";
import ChartBlockPreview from "./ChartBlockPreview";
import DownloadFileManagerPreview from "./DownloadFileManagerPreview";
import IconPreview from "./IconPreview";
import MapPreview from "./MapPreview";
import RVActionsListPreview from "./RVActionsListPreview";
import RVColumnsManagerPreview from "./RVColumnsManagerPreview";
import RVFiltersPreview from "./RVFiltersPreview";
import RVTabFiltersPreview from "./RVTabFiltersPreview";
import RVTablePreview from "./RVTablePreview";
import RvTilePreview from "./RvTilePreview";
import SearchBoxPreview from "./SearchBoxPreview";
import SingleChartPreview from "./SingleChartPreview";
import TableCatalogTogglerPreview from "./TableCatalogTogglerPreview";
import TablePreview from "./TablePreview";
import CustomComponentPreview from "./CustomComponentPreview";
import FormFieldPreview from "./FormFieldPreview";
import DVGroupPreview from "./DvGroupPreview";
import FormPreview from "./FormPreview";
import FilePreview from "./FilePreview";
import JsonToTablePreview from "./JsonToTablePreview";

const PositionedElementPreview = ({ node, nodeClassNames, type, saveConfig, onClickElement, device, tableId }) => {
  const { appId, orgId } = useParams();
  const config = node.config;

  const EnhancedBlockPreview = useMemo(() => withAppBlockData(BlockPreview), []);

  if (!config?.type) return null;

  const renderEnhancedBlock = (Component) => (
    <EnhancedBlockPreview config={config} device={device} type={type}>
      {(actualConfig) => <Component actualConfig={actualConfig} />}
    </EnhancedBlockPreview>
  );

  switch (config.type) {
    case "detailsview":
      return renderEnhancedBlock(DetailsViewPreview);

    case "recordsview":
      return renderEnhancedBlock(RecordsViewPreview);

    case "chart":
      if (config.chart_type) return <SingleChartPreview config={config} classes={nodeClassNames} />;
      if (config.chart_type === "") return <div>No Chart Selected</div>;
      return renderEnhancedBlock(ChartBlockPreview);

    case "activitycard":
      return (
        <NavigationButton type={type} config={config} navigationPath={`../../${appId}/applicationblock/${config.id}`}>
          <ActivityCardPreview config={config} classes={nodeClassNames} />
        </NavigationButton>
      );

    case "html":
      return <div dangerouslySetInnerHTML={{ __html: config.content }} />;

    case "spacer":
      return <div />;

    case "image":
      return <ImagePreview config={config} classes={nodeClassNames} />;

    case "label":
      return <LabelPreview config={config} classes={nodeClassNames} />;

    case "map":
      return <MapPreview config={config} classes={nodeClassNames} onClickElement={onClickElement} />;

    case "search_box":
      return <SearchBoxPreview config={config} classes={nodeClassNames} />;

    case "rv_columns_manager":
      return <RVColumnsManagerPreview config={config} classes={nodeClassNames} />;

    case "download_file":
      return <DownloadFileManagerPreview config={config} classes={nodeClassNames} />;

    case "rv_table_catalog_toggler":
      return <TableCatalogTogglerPreview config={config} classes={nodeClassNames} />;

    case "rv_actions_list":
      return <RVActionsListPreview config={config} classes={nodeClassNames} />;

    case "dropdown-navigate":
      return null;

    case "button":
    case "rv_action_button":
      return <ButtonPreview config={config} classes={nodeClassNames} />;

    case "button-group":
      return <ButtonGroupPreview config={config} classes={nodeClassNames} />;

    case "dv-group":
      return <DVGroupPreview config={config} device={device} type={type} />;

    case "json_to_table":
      return <JsonToTablePreview config={config} device={device} type={type} />;

    case "dropdown":
    case "rv_action_dropdown":
      return <DropdownPreview config={config} classes={nodeClassNames} />;

    case "field": {
      if (["image"].includes(config.field_type)) {
        return <ImagePreview config={config.image_placeholder} classes={nodeClassNames} />;
      }
      if (["file", "ocr"].includes(config.field_type)) {
        return <FilePreview config={config} classes={nodeClassNames} />;
      }
      return <FieldPreview config={config} classes={nodeClassNames} onClickElement={onClickElement} node={node} />;
    }

    case "icon":
      return <IconPreview config={config} classes={nodeClassNames} />;

    case "table":
      return (
        <TablePreview
          config={config}
          saveConfig={saveConfig}
          classes={nodeClassNames}
          onClickElement={onClickElement}
        />
      );

    case "rv_chart":
      return config.chart_type ? (
        <SingleChartPreview config={config} classes={nodeClassNames} />
      ) : (
        <div>No Chart Selected</div>
      );

    case "rv_tile":
    case "chart_tile":
      return <RvTilePreview config={config} saveConfig={saveConfig} classes={nodeClassNames} />;

    case "chart_filters":
    case "rv_filters":
      if (device === "mobile") {
        const icon = config?.icon?.split(" ")[0] || "";
        return (
          <div className={`rv-download-icon d_flex align_center justify_center ${nodeClassNames}`}>
            <MaterialIcon name={icon} />
          </div>
        );
      }
      return <RVFiltersPreview config={config} classes={nodeClassNames} />;

    case "rv_table":
      return (
        <RVTablePreview
          config={config}
          classes={nodeClassNames}
          saveConfig={saveConfig}
          onClickElement={onClickElement}
          type={type}
          node={node}
        />
      );

    case "rv_tab_filters":
      return <RVTabFiltersPreview config={config} classes={nodeClassNames} />;

    case "separator":
      return <SeparatorPreview config={config} classes={nodeClassNames} />;

    case "form-field":
      return <FormFieldPreview config={config} classes={nodeClassNames} />;

    case "custom-component":
      return (
        <NavigationButton
          type={type}
          config={config}
          navigationPath={`/organization/${orgId}/app/${appId}/template/${config.id}`}
        >
          <CustomComponentPreview config={config} classes={nodeClassNames} />
        </NavigationButton>
      );

    case "form":
      return <FormPreview config={config} tableId={tableId} />;

    default:
      return (
        <div className={nodeClassNames} style={{ height: "100%" }}>
          {config.name || config.type || ""}
        </div>
      );
  }
};

function useAppBlockPreviewData(config) {
  const { allApplicationBlockData } = useAppBlocksContext();

  if (!config?.id) return { error: "Invalid config", data: null };
  const data = allApplicationBlockData?.[config.id];
  if (!data) return { error: "Details view not found", data: null };

  return { data, error: null };
}

export const withAppBlockData = (WrappedComponent) => {
  return function ComponentWithAppBlockPreview({ config, ...rest }) {
    const { data, error } = useAppBlockPreviewData(config);

    if (error) {
      return <div>Error: {error}</div>;
    }

    if (!data) {
      return null;
    }

    return <WrappedComponent {...rest} config={config} actualConfig={data} />;
  };
};

export const BlockPreview = ({ config, type, actualConfig, children }) => {
  const { appId } = useParams();

  const navigationPath = `../../${appId}/applicationblock/${config.id}`;

  return (
    <NavigationButton type={type} config={config} actualConfig={actualConfig} navigationPath={navigationPath}>
      {children(actualConfig)}
    </NavigationButton>
  );
};

const NavigationButton = ({ children, type, config, actualConfig, navigationPath }) => {
  const navigate = useNavigate();

  const navigateToBlock = (e) => {
    e.stopPropagation();
    navigate(navigationPath);
  };

  return (
    <div className="hover_arrow">
      {type === "editable" && (
        <div className="arrow color_light" onClick={navigateToBlock}>
          {actualConfig && (
            <>
              <span className="text_transform_capitalize">{config.type || ""}</span>
              <MaterialIcon name="chevron_right" />
              <div>{actualConfig.name || ""}</div>
            </>
          )}
          <MaterialIcon name="arrow_outward" className="arrow_outward" />
        </div>
      )}
      {children}
    </div>
  );
};

export default PositionedElementPreview;
